<template>
    <UDrawer :open="modalOpen" @update:open="setOpen" direction="top">
        <template #header>
            <UContainer class="px-4 sm:px-6">
                <div class="flex items-center pt-4 pb-3 gap-3 sm:pt-8 sm:pb-4">
                    <UInput v-model="search" ref="searchInput" autofocus placeholder="Try 'blue', 'jacket', 'tops' or '1010'"
                        :size="isMobile ? 'md' : 'xl'" icon="workbook:search" variant="none"
                        class="w-full min-w-0 text-base sm:text-lg" @keydown="handleKeydown" />

                    <UButton icon="i-lucide-x" variant="link" color="neutral" size="sm" aria-label="Close search"
                        class="shrink-0" @click="close" />
                </div>
            </UContainer>
        </template>
        <template #body>
            <UContainer class="px-4 sm:px-0">
                <div class="pb-3">
                    <div v-if="search.length < 3" v-motion-pop class="text-sm text-neutral-400 py-8 text-center">
                        Type at least 3 characters to search.
                    </div>
                    <div v-else-if="loading" v-motion-pop class="py-8 text-center text-neutral-400">
                        Searching…
                    </div>
                    <div v-else-if="results.length === 0" class="py-8 text-center text-neutral-400">
                        No results found.
                    </div>
                    <div v-else>
                        <!-- CATEGORIES -->
                        <div v-if="categoryResults.length" class="mt-8">
                            <div class="text-xs uppercase tracking-wide text-neutral-400 mb-1">Categories</div>
                            <ul>
                                <li v-for="cat in categoryResults" :key="cat.slug">
                                    <NuxtLink :to="`/products/${cat.slug}`" class="search-row" @click="close">
                                        <span class="font-semibold">{{ cat.name }}</span>
                                        <span class="ml-2 text-neutral-400 text-xs">/products/{{ cat.slug }}</span>
                                    </NuxtLink>
                                </li>
                            </ul>
                        </div>
                        <!-- MAINNAV -->
                        <div v-if="navResults.length" class="mt-8">
                            <div class="text-xs uppercase tracking-wide text-neutral-400 mb-1">Pages</div>
                            <ul>
                                <li v-for="nav in navResults" :key="nav.to">
                                    <NuxtLink :to="nav.to" class="search-row" @click="close">
                                        <span class="font-semibold">{{ nav.label }}</span>
                                        <span v-if="nav.icon" class="ml-2">
                                            <Icon :name="nav.icon" />
                                        </span>
                                        <span class="ml-2 text-neutral-400 text-xs">{{ nav.to }}</span>
                                    </NuxtLink>
                                </li>
                            </ul>
                        </div>
                        <!-- PRODUCTS -->
                        <div v-if="productResults.length">
                            <div class="text-xs uppercase tracking-wide text-neutral-400 mt-4 mb-4">Products</div>
                            <UPageGrid>
                                <div v-motion-pop v-for="prod in productResults" :key="prod.style + (prod.colour || '')"
                                    class="search-product-card flex flex-col items-center col-span-4 lg:col-span-3 ">
                                    <NuxtLink :to="`/products/${prod.familySlug}#style-${prod.style}`" class="w-full"
                                        @click="close">
                                        <WorkbookProductImage :src="prod.image"
                                            :alt="prod.name + (prod.colour ? ' ' + prod.colour : '')"
                                            class="w-full bg-workbook-light-grey" />
                                    </NuxtLink>
                                    <div class="mt-2 text-center text-sm font-bold leading-tight">
                                        {{ prod.name }}
                                    </div>
                                    <div class="text-center text-xs text-neutral-600 mb-1">
                                        <span v-if="prod.colour">{{ prod.colour }}</span>
                                        <span v-if="prod.style" class="ml-2 text-neutral-400 text-xs">#{{ prod.style
                                            }}</span>
                                    </div>
                                    <!-- ModelSaveToggle integration -->
                                     
                                    <ModelSaveToggle
                                        v-if="modelForProduct(prod) && !modelForProduct(prod).NOTAvailableforPREOrder"
                                        :model="modelForProduct(prod)"
                                        size="sm"
                                        class="mt-1 mb-2"
                                    />
                                    <div v-else class="text-sm pt-4 font-semibold bg-white/90 px-3 py-1 border-red-300">
                                        Not available for pre-order
                                    </div>
                                </div>
                            </UPageGrid>
                        </div>
                        
                    </div>
                </div>
            </UContainer>
        </template>
    </UDrawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useCatalogStore } from '@/store/catalog'
import { heroImageForModel, imageForColour } from '@/utils/productImages'
import { useDebounceFn } from '@vueuse/core'
import WorkbookProductImage from '@/components/workbook/ProductImage.vue'
import ModelSaveToggle from '@/components/cart/ModelSaveToggle.vue'
import { usePlausible } from '@/composables/usePlausible'
import { useWindowSize } from '@vueuse/core'

const { width } = useWindowSize()

const isMobile = computed(() => width.value < 767)

const DEBOUNCE_DELAY = 550
const { trackEvent } = usePlausible()

const props = defineProps({
    open: { type: Boolean, default: false }
})
const emit = defineEmits(['update:open'])

const modalOpen = ref(props.open)
watch(() => props.open, val => { modalOpen.value = val })
function setOpen(val: boolean) {
    modalOpen.value = val
    emit('update:open', val)
}

const catalog = useCatalogStore()
const { data: mainnav } = await useAsyncData('mainnav', () =>
    queryCollection('mainnav').first()
)
const mainMenuItems = computed(() => Array.isArray(mainnav.value?.menuitems) ? mainnav.value.menuitems : [])

const search = ref('')
const loading = ref(false)
const results = ref<any[]>([])

const searchInput = ref<any>(null)

watch(modalOpen, async (val) => {
    if (val) {
        await nextTick(() => {
            // Försök hitta inre input-element om det är en komponent
            const el = searchInput.value
            if (el) {
                const inputEl = el.$el?.querySelector?.('input')
                if (inputEl && typeof inputEl.focus === 'function') {
                    inputEl.focus()
                }
            }
        })
    }
    if (!val) search.value = ''
})

function close() {
    setOpen(false)
    search.value = ''
}
function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') close()
}

const debouncedSearch = useDebounceFn(runSearch, DEBOUNCE_DELAY)

watch(search, () => {
    if (search.value.length >= 3) {
        loading.value = true
        debouncedSearch()
    } else {
        results.value = []
    }
})

function normalize(str: string): string {
    return String(str || '').toLowerCase()
}

function runSearch() {
    const query = normalize(search.value)
    if (query.length < 3) {
        results.value = []
        loading.value = false
        return
    }

    const techBooleans = [
        { key: 'helios', label: 'Helios' },
        { key: 'wtv', label: 'WTV' },
        { key: 'airmap', label: 'Airmap' },
    ]
    const techSearch = techBooleans.find(tb => query === tb.key || query === normalize(tb.label))

    const res: any[] = []

    const catHits = catalog.categories
        .filter(cat =>
            normalize(cat.Name).includes(query) ||
            normalize(cat.Slug).includes(query)
        )
        .map(cat => ({
            type: 'category',
            name: cat.Name,
            slug: cat.Slug
        }))

    const prodHits: any[] = []
    for (const model of catalog.models) {
        if (
            normalize(model.name).includes(query) ||
            normalize(model.Style).includes(query) ||
            normalize(model.ProductFamilySlug).includes(query)
        ) {
            prodHits.push({
                type: 'product',
                name: model.name,
                style: model.Style,
                familySlug: model.ProductFamilySlug,
                image: heroImageForModel(model)
            })
            continue
        }
        for (const variant of model.variants) {
            if (
                normalize(variant.SKU).includes(query) ||
                normalize(variant.Colour).includes(query)
            ) {
                prodHits.push({
                    type: 'product',
                    name: model.name,
                    style: model.Style,
                    familySlug: model.ProductFamilySlug,
                    image: imageForColour(model, variant.Colour),
                    colour: variant.Colour
                })
                break
            }
        }
        if (techSearch && model[techSearch.label]) {
            prodHits.push({
                type: 'product',
                name: model.name,
                style: model.Style,
                familySlug: model.ProductFamilySlug,
                image: heroImageForModel(model)
            })
            continue
        }
        if (
            (model.ProductMaterialType && normalize(model.ProductMaterialType).includes(query)) ||
            (model.Sustainability && normalize(model.Sustainability).includes(query))
        ) {
            prodHits.push({
                type: 'product',
                name: model.name,
                style: model.Style,
                familySlug: model.ProductFamilySlug,
                image: heroImageForModel(model)
            })
            continue
        }
    }

    const navHits = mainMenuItems.value
        .filter(item =>
            item &&
            (normalize(item.label || '').includes(query) ||
                (item.to && normalize(item.to).includes(query)))
        )
        .map(item => ({
            type: 'nav',
            label: item.label,
            to: item.to,
            icon: item.icon
        }))

    results.value = [...catHits, ...prodHits, ...navHits]
    loading.value = false
    
    // Track search event
    trackEvent('Search', {
        query: search.value,
        resultCount: results.value.length,
        categoryResults: catHits.length,
        productResults: prodHits.length,
        navResults: navHits.length
    })
}

// Get ProductModel from catalog for the given search result
function modelForProduct(prod: any) {
    // We use style as key for ProductModel
    if (!prod || !prod.style) return null
    return catalog.modelByStyle?.(prod.style) || null
}

const categoryResults = computed(() => results.value.filter(r => r.type === 'category'))
const productResults = computed(() => results.value.filter(r => r.type === 'product'))
const navResults = computed(() => results.value.filter(r => r.type === 'nav'))
</script>

<style scoped>
@reference "../assets/css/main.css";

.search-row {
    @apply flex items-center gap-2 py-2 px-1 transition-colors cursor-pointer;
}

.search-product-card {
    @apply p-2 border border-transparent transition-colors;
}
</style>
